import type { RealtimeService } from './RealtimeService';
import { plan, type Actual, type Desired, type Effect } from './channelPlan';
import type { RealtimeEvent } from './eventDefinition';
import { PresenceDocument, type PresenceDoc } from './presenceDocument';
import type { ChannelError, InboundChannelMessage, PresenceUser } from './protocol';
import type { ChannelState } from './types';

// One ladder, shared by the two ways an attempt can fail to produce a subscription: a capability
// token we cannot fetch, and a frame the server never answers.
const RETRY_DELAYS = [500, 2000, 5000, 30000];
const DEADLINE = 10000;

// Every phase that waits on a result gets a deadline. That is where subscribe's comes from: not a
// special case, but the same rule authorize and leave already followed.
const WAITING: ReadonlySet<ChannelState> = new Set<ChannelState>(['authorizing', 'subscribing', 'unsubscribing']);

// Wrapped so two hooks registering the same function reference stay distinct interests.
type ListenerEntry = { handler: (event: RealtimeEvent<unknown>) => void };

// Shared so repeated "no presence" transitions compare equal and don't wake the store.
const EMPTY_PRESENCE = Object.freeze([]) as unknown as PresenceUser[];

function reportListenerError(error: unknown): void {
  const reportError = (globalThis as typeof globalThis & { reportError?: (error: unknown) => void }).reportError;
  if (reportError) reportError(error);
  else console.error('[Realtime] Event listener failed', error);
}

/**
 * One internal, reference-counted session for a server-side channel name.
 *
 * The session holds three values — what consumers want, what we believe the server knows, and the
 * cached capability token — and executes what `plan` derives from them. Every input writes one
 * field and calls `run()`; nothing here decides its own next send.
 */
export class ChannelSession {
  private desired: Desired = { subscribed: false, presence: null };
  private actual: Actual = { phase: 'unsubscribed' };
  private token?: string;

  // Keyed by connection id so a join for a connection already in the subscribe snapshot updates it
  // instead of duplicating it, and leave/update are lookups rather than array rebuilds.
  private roster = new Map<string, PresenceUser>();
  // The array React compares. Rebuilt when the map changes and held until it changes again.
  private rosterSnapshot: PresenceUser[] = EMPTY_PRESENCE;

  private eventListeners = new Map<string, Set<ListenerEntry>>();
  private channelSubscribers = new Set<() => void>();

  // What we want the server to know about this connection, composed from hook-owned slices.
  private presence = new PresenceDocument();

  private tokenRequest: AbortController | null = null;
  private failures = 0;

  // One timer at a time, remembering the phase it was armed in so re-running the plan inside a
  // phase never extends its clock.
  private timer: ReturnType<typeof setTimeout> | null = null;
  private timerPhase: ChannelState | null = null;

  private leaseCount = 0;

  constructor(
    public readonly name: string,
    private service: RealtimeService,
  ) {}

  /** Acquire one interest. The returned release is idempotent. */
  acquire(): () => void {
    this.leaseCount += 1;
    this.desired.subscribed = true;
    this.run();

    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.leaseCount -= 1;
      if (this.leaseCount > 0) return;
      // Do not believe an unmount for one macrotask. A Strict Mode remount reclaims the session
      // before this runs, and because the desired state never flipped, nothing was ever planned to
      // undo: the subscription, the in-flight token request and the retry ladder all just stand.
      setTimeout(() => {
        if (this.leaseCount === 0) this.releaseNow();
      });
    };
  }

  /** Nobody wants this session and the server knows nothing about it. Safe to drop. */
  get isIdle(): boolean {
    return !this.desired.subscribed && this.actual.phase === 'unsubscribed';
  }

  /** Register a fan-out listener for exactly one event name. */
  listen<T>(eventName: string, handler: (event: RealtimeEvent<T>) => void): () => void {
    const entry: ListenerEntry = { handler: handler as (event: RealtimeEvent<unknown>) => void };
    let listeners = this.eventListeners.get(eventName);
    if (!listeners) {
      listeners = new Set();
      this.eventListeners.set(eventName, listeners);
    }
    listeners.add(entry);

    const release = this.acquire();
    return () => {
      const current = this.eventListeners.get(eventName);
      current?.delete(entry);
      if (current?.size === 0) this.eventListeners.delete(eventName);
      release();
    };
  }

  subscribeStore = (callback: () => void): (() => void) => {
    this.channelSubscribers.add(callback);
    const release = this.acquire();
    return () => {
      this.channelSubscribers.delete(callback);
      release();
    };
  };

  // Two snapshots rather than one composite object: both are values React can compare, so a
  // presence_update does not re-render a channel-state observer and vice versa.
  getStateSnapshot = (): ChannelState => this.actual.phase;
  getPresenceSnapshot = (): PresenceUser[] => this.rosterSnapshot;

  /** Set or remove one hook-owned slice of the desired presence document. */
  setPresenceSlice<T>(owner: symbol, name: string, data: T | null, legacyProjection?: (data: T) => PresenceDoc): void {
    // An unchanged slice owes the server nothing; a re-emitted cell selection stops here.
    if (!this.presence.set(owner, name, data, legacyProjection)) return;
    this.desired.presence = this.presence.document;
    // No lease left means a release is already queued, and it unsubscribes. A hook clearing its
    // slice on the way out would otherwise spend a frame describing a channel we leave this turn.
    // The write still lands, so a remount that reclaims the session inside the grace window sends
    // whatever the document ended up being.
    if (this.leaseCount === 0) return;
    this.run();
  }

  handleConnect(): void {
    this.run();
  }

  handleDisconnect(): void {
    this.clearTimer();
    this.failures = 0;
    this.clearRoster();
    // Desired presence slices remain while their hooks keep leases and are sent after resubscribe.
    this.run();
  }

  handleMessage(message: InboundChannelMessage): void {
    switch (message.type) {
      case 'error':
        this.handleError(message);
        break;

      case 'subscribed':
        // The leave already went out; adopting a subscription we asked to end would strand it.
        if (this.actual.phase === 'unsubscribing') break;
        this.failures = 0;
        this.setRoster(message.payload.presence);
        // A fresh subscription carries no presence for this connection. Recording that is what
        // makes the run below send whatever slices are held.
        this.setActual({ phase: 'subscribed', presence: null });
        this.run();
        break;

      case 'unsubscribed':
        this.failures = 0;
        this.token = undefined;
        this.clearRoster();
        this.setActual({ phase: 'unsubscribed' });
        this.run();
        break;

      // A join for a connection the subscribe snapshot already carried updates it rather than
      // appending a second entry for the same connection.
      case 'join':
      case 'presence_update':
        this.roster.set(message.payload.user.connection_id, message.payload.user);
        this.publishRoster();
        break;

      case 'leave':
        if (this.roster.delete(message.payload.user.connection_id)) this.publishRoster();
        break;

      case 'message': {
        // A snapshot, so a registration change during dispatch cannot skip another listener.
        const listeners = [...(this.eventListeners.get(message.payload.event) ?? [])];
        const event: RealtimeEvent<unknown> = {
          data: message.payload.data,
          revision: message.payload.revision,
          originConnectionId: message.payload.origin_connection_id,
        };
        for (const { handler } of listeners) {
          try {
            handler(event);
          } catch (error) {
            reportListenerError(error);
          }
        }
        break;
      }
    }
  }

  private handleError(message: ChannelError): void {
    switch (message.payload.code) {
      case 'TOKEN_EXPIRED':
      case 'TOKEN_INVALID':
      case 'ISSUER_MISMATCH':
        console.warn(`[Realtime] Capability token ${message.payload.code} for ${this.name}; refetching automatically.`);
        this.token = undefined;
        this.recover();
        break;
      case 'MALFORMED':
        // A malformed frame while subscribing can only be the subscribe request, whose only
        // dynamic input is the cached capability token. Once subscribed, malformed instead refers
        // to another channel operation and must not discard a usable token.
        if (this.actual.phase === 'subscribing') {
          console.warn(`[Realtime] Malformed subscribe for ${this.name}; refetching capability token.`);
          this.token = undefined;
          this.recover();
          break;
        }
        console.error(`[Realtime] Channel error ${message.payload.code} on ${this.name}: ${message.payload.message}`);
        break;
      case 'NOT_SUBSCRIBED':
        console.warn(`[Realtime] NOT_SUBSCRIBED for ${this.name}; recovering.`);
        this.recover();
        break;
      default:
        console.error(`[Realtime] Channel error ${message.payload.code} on ${this.name}: ${message.payload.message}`);
    }
  }

  /**
   * A refusal we know how to answer. It takes the ladder rather than going straight back onto the
   * wire: these errors produce no acknowledgement that resets the ladder, so a token the server
   * always refuses — a clock skew, a misconfigured issuer — would otherwise be an unbounded
   * fetch/subscribe loop running at the speed of the round trip.
   *
   * `authorizing` is the phase this waits in for the same reason the token deadline waits there:
   * the plan does not act on it, so nothing is re-sent while the delay runs.
   */
  private recover(): void {
    this.setActual({ phase: 'authorizing' });
    this.backOff();
  }

  /**
   * The last interest is really gone. Everything held on its behalf goes with it, in one place:
   * the request, the ladder, the capability token, the desired presence slices and the roster.
   * That is what makes a released session inert whether or not the registry still has the entry,
   * so dropping the entry later can be a sweep rather than a correctness obligation.
   */
  private releaseNow(): void {
    this.abortToken();
    this.clearTimer();
    this.failures = 0;
    this.token = undefined;
    this.presence.clear();
    this.clearRoster();
    this.desired = { subscribed: false, presence: null };
    this.run();
  }

  /** One step: derive the next state, notify on a phase change, then say what it implies. */
  private run(): void {
    const next = plan({
      connected: this.service.isConnected,
      desired: this.desired,
      actual: this.actual,
      token: this.token,
    });

    this.setActual(next.actual);
    for (const effect of next.effects) this.execute(effect);
    this.armDeadline();
  }

  private execute(effect: Effect): void {
    switch (effect.do) {
      case 'fetchToken':
        this.fetchToken();
        break;
      case 'abortToken':
        this.abortToken();
        break;
      case 'dropToken':
        this.token = undefined;
        break;
      case 'subscribe':
        this.service.send({ type: 'subscribe', payload: { channel: this.name, jwt: effect.token } });
        break;
      case 'unsubscribe':
        this.service.send({ type: 'unsubscribe', payload: { channel: this.name } });
        break;
      case 'setPresence':
        this.service.send({ type: 'set_presence', payload: { channel: this.name, data: effect.document } });
        break;
    }
  }

  /**
   * The token request is an effect, not an await inside the machine: its completion writes the
   * actual state and re-enters through `run` like any other input, so no guard has to survive a
   * suspension point.
   */
  private fetchToken(): void {
    const request = new AbortController();
    this.tokenRequest = request;

    this.service.options.loadChannelToken(this.name, request.signal).then(
      (token) => {
        if (request.signal.aborted || this.tokenRequest !== request) return;
        this.tokenRequest = null;
        this.token = token;
        // Leave the waiting phase so the plan can act on the token we now hold.
        this.setActual({ phase: 'unsubscribed' });
        this.run();
      },
      (error) => {
        if (request.signal.aborted || this.tokenRequest !== request) return;
        this.tokenRequest = null;
        console.error(`[Realtime] Failed to fetch capability token for ${this.name}`, error);
        this.backOff();
      },
    );
  }

  private abortToken(): void {
    this.tokenRequest?.abort();
    this.tokenRequest = null;
  }

  /**
   * A phase that waits on a result gets a deadline; a phase that waits on nothing clears it.
   * A phase already counting down — whether toward a deadline or a retry — keeps its clock.
   */
  private armDeadline(): void {
    const phase = this.actual.phase;
    if (!WAITING.has(phase)) {
      this.clearTimer();
      return;
    }
    if (this.timerPhase === phase) return;
    this.schedule(DEADLINE, () => this.onDeadline());
  }

  private onDeadline(): void {
    switch (this.actual.phase) {
      case 'authorizing':
        console.warn(`[Realtime] Capability token for ${this.name} did not arrive in time`);
        this.abortToken();
        this.backOff();
        break;
      case 'subscribing':
        console.warn(`[Realtime] No subscribe acknowledgement for ${this.name}`);
        this.backOff();
        break;
      case 'unsubscribing':
        console.warn(`[Realtime] No unsubscribe acknowledgement for ${this.name}; forcing it inactive`);
        this.token = undefined;
        this.setActual({ phase: 'unsubscribed' });
        this.run();
        break;
    }
  }

  /**
   * Wait before trying again, without leaving the waiting phase. That is what stops the plan from
   * asking for a second token, or resending a subscribe, while one is still owed.
   */
  private backOff(): void {
    const delay = RETRY_DELAYS[Math.min(this.failures, RETRY_DELAYS.length - 1)];
    this.failures += 1;
    console.warn(`[Realtime] Retrying ${this.name} in ${delay}ms`);
    this.schedule(delay, () => {
      this.setActual({ phase: 'unsubscribed' });
      this.run();
    });
  }

  private schedule(delay: number, action: () => void): void {
    this.clearTimer();
    this.timerPhase = this.actual.phase;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.timerPhase = null;
      action();
    }, delay);
  }

  private clearTimer(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.timerPhase = null;
  }

  private setActual(next: Actual): void {
    const changed = next.phase !== this.actual.phase;
    this.actual = next;
    if (changed) this.notify();
  }

  private setRoster(users: PresenceUser[]): void {
    this.roster = new Map(users.map((user) => [user.connection_id, user]));
    this.publishRoster();
  }

  private clearRoster(): void {
    if (this.roster.size === 0) return;
    this.roster.clear();
    this.publishRoster();
  }

  /** The map is the source of truth; this publishes the array value React compares. */
  private publishRoster(): void {
    const next = this.roster.size === 0 ? EMPTY_PRESENCE : [...this.roster.values()];
    if (next === this.rosterSnapshot) return;
    this.rosterSnapshot = next;
    this.notify();
  }

  private notify(): void {
    this.channelSubscribers.forEach((callback) => callback());
  }
}
